import type { TypePolicies } from '@apollo/client';
import { GraphQLProvider } from '@tbitop/api';
import { useGlobalGraphQLErrorHandler } from '@tbitop/error-handling';
import { isUndefined } from 'lodash-es';
import type { PropsWithChildren } from 'react';
import { clientVersionVar } from '.';
import { environment } from '../../environments/environment';

type PaginatedResult = {
  aktuelleSeite: unknown[];
};

const PAGINATED_FIELD_POLICY = {
  keyArgs: [
    'suchbegriff',
    'filter',
    'sortierung',
    'sortierrichtung',
  ],
  // Concatenate the incoming list items with the existing list items only for pagination (fetchMore).
  // For initial queries and refetches (seite === 1), replace the cache entirely to avoid stale data.
  merge(existing: PaginatedResult, incoming: PaginatedResult, { args }: { args: Record<string, unknown> | null }) {
    if (isUndefined(existing) || !args || args.seite === 1) {
      return incoming;
    }

    return {
      ...incoming,
      aktuelleSeite: [
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        ...existing.aktuelleSeite!,
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        ...incoming.aktuelleSeite!,
      ],
    };
  },
  read(existing: PaginatedResult): PaginatedResult {
    return existing;
  },
};

const typePolicies: TypePolicies = {
  Versionsinformationen: {
    fields: {
      clientVersion: {
        read: () => clientVersionVar(),
      },
    },
  },
  Query: {
    fields: {
      objekteSeitenweise: PAGINATED_FIELD_POLICY,
      neueObjekteFuerKampagne: { ...PAGINATED_FIELD_POLICY, keyArgs: [...PAGINATED_FIELD_POLICY.keyArgs, 'kampagne'] },
      aufgabenSeitenweise: PAGINATED_FIELD_POLICY,
    },
  },
};

export const SalesPlannerGraphQLProvider = ({ children }: PropsWithChildren<unknown>) => (
  <GraphQLProvider
    id="sales-planner"
    uri={environment.graphQLEndpoint}
    connectToDevTools={true}
    typePolicies={typePolicies}
    errorHandler={useGlobalGraphQLErrorHandler()}
  >
    {children}
  </GraphQLProvider>
);
